import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { superadminApi } from '../lib/api';
import { setToastOverrides } from '../lib/toastOverrides';
import { MESSAGE_CATALOG, MESSAGE_AREAS } from '../lib/messages';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { Loader2, Search, RotateCcw, Save, Play } from 'lucide-react';

// Superadmin editor for the in-app toast copy. Defaults come from the generated
// catalogue (lib/messages.js); overrides are stored server-side keyed by the
// default text and applied live via lib/toastOverrides. Live-data messages
// (their text varies) are shown for reference but can't be edited by exact text.
const UiMessagesManager = () => {
  const [overrides, setOverrides] = useState({});
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [query, setQuery] = useState('');
  const [area, setArea] = useState('all');
  const [scope, setScope] = useState('all');       // all | customer | internal
  const [type, setType] = useState('all');         // all | editable | live

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await superadminApi.listUiMessageOverrides();
      const map = (r.data && typeof r.data === 'object') ? r.data : {};
      setOverrides(map); setDrafts(map);
    } catch (e) {
      toast.error('Could not load messages');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setDraft = (key, val) => setDrafts((d) => ({ ...d, [key]: val }));

  const persist = async (key, val) => {
    setSavingKey(key);
    try {
      await superadminApi.saveUiMessage(key, val);
      const next = { ...overrides };
      if (val) next[key] = val; else delete next[key];
      setOverrides(next); setToastOverrides(next);
      toast.success(val ? 'Message saved' : 'Restored to default');
    } catch (e) {
      toast.error(e.response?.status === 403 ? "You don't have permission to edit messages" : 'Could not save the message');
    } finally { setSavingKey(null); }
  };
  const save = (key) => persist(key, (drafts[key] || '').trim());
  const reset = (key) => { setDraft(key, ''); persist(key, ''); };

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = MESSAGE_CATALOG.filter((m) => {
      if (area !== 'all' && m.area !== area) return false;
      if (scope !== 'all' && m.scope !== scope) return false;
      if (type === 'editable' && m.live) return false;
      if (type === 'live' && !m.live) return false;
      if (q && !(m.text.toLowerCase().includes(q) || (drafts[m.text] || '').toLowerCase().includes(q) || m.area.toLowerCase().includes(q))) return false;
      return true;
    });
    const by = {};
    filtered.forEach((m) => { (by[m.area] = by[m.area] || []).push(m); });
    return Object.keys(by).sort().map((a) => ({ area: a, items: by[a] }));
  }, [query, area, scope, type, drafts]);

  const matchCount = groups.reduce((n, g) => n + g.items.length, 0);
  const customCount = Object.keys(overrides).length;
  const selCls = 'input-hive h-10 px-2.5 text-sm bg-input';

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4">
      <div className="card-hive p-4 space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search messages…" className="input-hive pl-9" />
          </div>
          <select value={area} onChange={(e) => setArea(e.target.value)} className={selCls} title="Filter by area">
            <option value="all">All areas</option>
            {MESSAGE_AREAS.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <select value={scope} onChange={(e) => setScope(e.target.value)} className={selCls} title="Filter by audience">
            <option value="all">All audiences</option>
            <option value="customer">Customer-facing</option>
            <option value="internal">Platform only</option>
          </select>
          <select value={type} onChange={(e) => setType(e.target.value)} className={selCls} title="Filter by type">
            <option value="all">All types</option>
            <option value="editable">Editable</option>
            <option value="live">Live data</option>
          </select>
        </div>
        <p className="text-xs text-muted-foreground">
          Showing {matchCount} of {MESSAGE_CATALOG.length} messages · {customCount} customised.
          A <span className="text-purple-600 font-medium">Platform</span> message is seen only by your team (have fun);
          a <span className="text-amber-600 font-medium">Customer</span> one shows to customers, so keep it professional.
          <span className="text-muted-foreground/80"> Live-data messages carry values like a count, so they're shown for reference but not editable.</span>
        </p>
      </div>

      {groups.map(({ area: a, items }) => (
        <div key={a} className="card-hive p-4">
          <h3 className="font-heading font-semibold text-sm mb-3">{a}</h3>
          <div className="space-y-3">
            {items.map((m) => {
              const isCustom = !!overrides[m.text];
              const dirty = (drafts[m.text] || '') !== (overrides[m.text] || '');
              const scopePill = (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${m.scope === 'internal' ? 'bg-purple-500/12 text-purple-600' : 'bg-amber-500/12 text-amber-600'}`}>
                  {m.scope === 'internal' ? 'Platform' : 'Customer'}
                </span>
              );
              if (m.live) {
                return (
                  <div key={m.text} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2 md:items-center border-b border-border/50 pb-3 last:border-0 last:pb-0 opacity-80">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">{scopePill}
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-secondary text-muted-foreground">Live data</span>
                      </div>
                      <p className="text-sm mt-1 break-words">{m.text}</p>
                    </div>
                    <div className="md:col-span-2 text-xs text-muted-foreground italic self-center">
                      Contains live data (the {'{…}'} part) — shown for reference, not editable here.
                    </div>
                  </div>
                );
              }
              return (
                <div key={m.text} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2 md:items-center border-b border-border/50 pb-3 last:border-0 last:pb-0">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">{scopePill}
                      {isCustom && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary">Custom</span>}
                    </div>
                    <p className="text-sm mt-1 break-words">{m.text}</p>
                  </div>
                  <Input
                    value={drafts[m.text] || ''}
                    onChange={(e) => setDraft(m.text, e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && dirty) save(m.text); }}
                    placeholder="Custom message…"
                    className="input-hive"
                  />
                  <div className="flex items-center gap-1 justify-end">
                    <button type="button" title="Preview as a toast"
                      onClick={() => toast.success((drafts[m.text] || '').trim() || m.text)}
                      className="p-2 rounded-md text-muted-foreground hover:bg-secondary/50"><Play className="w-4 h-4" /></button>
                    <button type="button" title="Restore default" disabled={!isCustom && !(drafts[m.text] || '')}
                      onClick={() => reset(m.text)}
                      className="p-2 rounded-md text-muted-foreground hover:bg-secondary/50 disabled:opacity-40"><RotateCcw className="w-4 h-4" /></button>
                    <Button size="sm" className="btn-primary" disabled={!dirty || savingKey === m.text} onClick={() => save(m.text)}>
                      {savingKey === m.text ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
      {matchCount === 0 && <p className="text-sm text-muted-foreground text-center py-8">No messages match those filters.</p>}
    </div>
  );
};

export default UiMessagesManager;
